import { observable } from "@legendapp/state";
import type * as Ably from "ably";

import { syncedCrudPersist } from "~/libs/legend-state";
import { getOrgChannel } from "~/libs/pubsub.lib";
import { trpc } from "~/libs/trpc.lib";
import { appDataReady$ } from "../app-data-ready";
import type { BoardMember } from "./board-members.types";

/**
 * Org-scoped store of Phase 6 board guests (one row per
 * `board_members` row, joined with `users.email` / `users.name`).
 *
 * `create` is omitted on purpose: members are created server-side by
 * `boardInvitations.accept`. The accept handler broadcasts the new row
 * with the joined user fields, so every share dialog observer learns
 * about the new guest without an explicit client call.
 *
 * `delete` maps to `boardInvitations.removeMember`, which deletes the
 * row and broadcasts a `_deleted: true` tombstone.
 */
export const boardMembers$ = observable(
  syncedCrudPersist<BoardMember>({
    persist: {
      name: "board-members",
    },
    waitFor: appDataReady$,
    list: async () => {
      return await trpc.boardInvitations.listMembers.query();
    },
    delete: async (input) => {
      if (!input.id) return;
      await trpc.boardInvitations.removeMember.mutate({
        memberId: input.id,
      });
    },
    subscribe: ({ update }) => {
      const channel = getOrgChannel();
      if (!channel) return;

      const handler = (message: Ably.Message) => {
        const items = Array.isArray(message.data)
          ? message.data
          : [message.data];
        if (items.length === 0) return;
        update({
          mode: "merge",
          value: items,
        });
      };

      channel.subscribe("board-members", handler);

      return () => {
        channel.unsubscribe("board-members", handler);
      };
    },
  }),
);
